import defaultStorroLogo from 'assets/logo-gray.svg';
import defaultAgencyDriveLogo from 'assets/agency-drive-logo-gray.svg';
import { useRealmContext } from 'context/RealmContext';
import { useStorroApi } from 'context/StorroApiContext';
import useTheme from 'hooks/UseTheme';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { Realm } from 'types';
import { RealmPlan } from 'types/Payment';
import { logger } from 'util/Logger';

export default function useRealm(): {
  loadRealms: () => Promise<Realm[]>;
  loadRealm: (realmId: number, signal?: AbortSignal) => Promise<void>;
  realm: Realm | undefined;
  realms: Realm[];
  setRealms: Dispatch<SetStateAction<Realm[]>>;
  setRealm: (realm: Realm | undefined) => void;
  loading: boolean;
  realmCount: number | undefined;
  initialLoaded: boolean;
  realmLogo: string; // this is either the custom logo or the Storro fallback logo
  realmCustomLogo: string | undefined;
  realmPlan: RealmPlan | undefined;
  loadRealmPlan: (realm: Realm, abortSignal?: AbortSignal) => Promise<RealmPlan | undefined>;
} {
  const [loading, setLoading] = useState<boolean>(false);
  const [customLogo, setCustomLogo] = useState<string>();

  const { isAgencyTheme } = useTheme();
  const { storroApi } = useStorroApi();
  const {
    realm,
    realms,
    setRealm: _setRealm,
    setRealms,
    setRealmCount,
    realmCount,
    initialLoaded,
    setInitialLoaded,
    setRealmLogo,
    realmLogo,
    realmPlan,
    setRealmPlan,
  } = useRealmContext();

  const defaultLogo = isAgencyTheme ? defaultAgencyDriveLogo : defaultStorroLogo;

  /**
   * Load all realms
   */
  const loadRealmPlan = useCallback(
    async (realm: Realm, abortSignal?: AbortSignal): Promise<RealmPlan | undefined> => {
      try {
        const res = await storroApi.getPlan(realm.id, abortSignal);
        setRealmPlan(res);
        return res;
      } catch (error) {
        logger.error(error);
      }
    },
    [setRealmPlan, storroApi],
  );

  const setRealm = useCallback(
    (realm: Realm | undefined) => {
      _setRealm(realm);
      if (realm && realm.isAdmin) {
        loadRealmPlan(realm);
      } else {
        setRealmPlan(undefined);
      }
    },
    [_setRealm, loadRealmPlan, setRealmPlan],
  );

  /**
   * Load all realms
   */
  const loadRealms = useCallback(async () => {
    setLoading(true);
    try {
      const realms = await storroApi.listRealms();
      setRealms(realms);
      setRealmCount(realms.length);
      setInitialLoaded(true);

      return Promise.resolve(realms);
    } catch (e) {
      setRealms([]);
      setRealm(undefined);
      setRealmCount(0);
      logger.error(e);
      return Promise.reject();
    } finally {
      setLoading(false);
    }
  }, [setInitialLoaded, setRealm, setRealmCount, setRealms, storroApi]);

  /**
   * Load a single realm
   */
  const loadRealm = useCallback(
    async (realmId: number, signal?: AbortSignal) => {
      setLoading(true);
      try {
        const realm = await storroApi.listRealm(realmId, signal);
        if (!signal?.aborted) {
          setRealm(realm);
        }

        return Promise.resolve();
      } catch (e) {
        if (!signal?.aborted) {
          logger.error(e);
          return Promise.reject();
        }
      } finally {
        setLoading(false);
      }
    },
    [setRealm, storroApi],
  );

  /**
   * Check if a custom realm logo exists, if so set this as the logo
   */
  useEffect(() => {
    if (realm && realm.logoHash) {
      const customLogo = storroApi.getLogoUrl(realm.id, realm.logoHash);
      setCustomLogo(customLogo);
      setRealmLogo(customLogo);
    } else {
      setCustomLogo(undefined);
      setRealmLogo(defaultLogo);
    }
  }, [defaultLogo, realm, setRealmLogo, storroApi]);

  return {
    loadRealms,
    loadRealm,
    realm,
    realms,
    setRealm,
    setRealms,
    loading,
    realmCount,
    initialLoaded,
    realmLogo: realmLogo ?? defaultLogo,
    realmCustomLogo: customLogo,
    realmPlan,
    loadRealmPlan,
  };
}
